import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { Sparkles, Loader2, X, ChevronRight } from "lucide-react";

/**
 * Docket Auto-Job — asks the backend which job this docket most likely belongs to,
 * based on historical supplier→job patterns and token overlap with job name/address.
 *
 * Behaviour:
 *   - Debounced 500ms on supplier/description/notes/entry_date changes.
 *   - Auto-applies the top guess ONLY when: (a) user hasn't manually touched job_id in this
 *     session; (b) currentJobId is empty OR still equals the default activeJobId; (c) confidence >= 75.
 *   - Otherwise renders a subtle chip the user can tap to apply.
 */
export default function AutoJobSuggestion({
  form,
  jobs,
  activeJobId,
  jobTouched,
  onApply,
}) {
  const [busy, setBusy] = useState(false);
  const [guess, setGuess] = useState(null); // { job_id, job_name, confidence, reason, alternatives }
  const [dismissedFor, setDismissedFor] = useState(null); // supplier string user dismissed
  const autoAppliedRef = useRef(false);
  const lastSupplierRef = useRef("");
  const abortRef = useRef(null);

  const supplier = String(form.supplier || "").trim();
  const description = String(form.description || "").trim();
  const notes = String(form.notes || "").trim();

  // Reset auto-apply guard whenever the supplier text changes to something new
  useEffect(() => {
    if (supplier !== lastSupplierRef.current) {
      autoAppliedRef.current = false;
      lastSupplierRef.current = supplier;
    }
  }, [supplier]);

  useEffect(() => {
    // Only request if we have any signal to work with
    if (!supplier && !description && !notes) {
      setGuess(null);
      return undefined;
    }
    if (dismissedFor && dismissedFor === supplier) {
      return undefined;
    }
    const ctrl = new AbortController();
    abortRef.current?.abort?.();
    abortRef.current = ctrl;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.post(
          "/jobs/suggest",
          { supplier, description, notes, entry_date: form.entry_date },
          { signal: ctrl.signal }
        );
        if (ctrl.signal.aborted) return;
        setGuess(data && data.job_id ? data : null);
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          devLog.warn("job suggest failed", err);
        }
      } finally {
        if (!ctrl.signal.aborted) setBusy(false);
      }
    }, 500);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [supplier, description, notes, form.entry_date, dismissedFor]);

  // Auto-apply when confident and user hasn't picked one
  useEffect(() => {
    if (!guess || autoAppliedRef.current) return;
    if (jobTouched) return;
    const currentEmpty = !form.job_id;
    const currentIsDefault = form.job_id && form.job_id === activeJobId;
    if ((currentEmpty || currentIsDefault) && guess.confidence >= 75) {
      autoAppliedRef.current = true;
      onApply?.(guess.job_id, { auto: true, suggestion: guess });
    }
  }, [guess, jobTouched, form.job_id, activeJobId, onApply]);

  if (!guess) {
    if (busy) {
      return (
        <div
          data-testid="rv-autojob-loading"
          className="text-[11px] text-white/40 inline-flex items-center gap-1.5"
        >
          <Loader2 className="w-3 h-3 animate-spin" /> AI checking job history…
        </div>
      );
    }
    return null;
  }

  const matchedCurrent = guess.job_id === form.job_id;
  const known = jobs.some((j) => j.id === guess.job_id);
  if (!known) return null;

  return (
    <div
      className={`mt-2 rounded-xl border ${
        matchedCurrent
          ? "border-[#34D399]/30 bg-[#34D399]/5"
          : "border-[#FF9500]/30 bg-[#FF9500]/5"
      } p-2.5`}
      data-testid="rv-autojob"
    >
      <div className="flex items-center gap-2">
        <Sparkles
          className={`w-3.5 h-3.5 shrink-0 ${
            matchedCurrent ? "text-[#34D399]" : "text-[#FF9500]"
          }`}
        />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-heading text-white/50">
            {matchedCurrent ? "AI picked this job" : "AI suggests"}
          </div>
          <div className="text-sm font-semibold truncate">
            {guess.job_name}{" "}
            <span
              className={`text-[10px] font-normal ${
                matchedCurrent ? "text-[#34D399]" : "text-[#FF9500]"
              }`}
            >
              · {guess.confidence}%
            </span>
          </div>
          <div className="text-[11px] text-white/50 truncate">{guess.reason}</div>
        </div>
        {matchedCurrent ? (
          <button
            type="button"
            data-testid="rv-autojob-dismiss"
            onClick={() => {
              setDismissedFor(supplier || "__");
              setGuess(null);
              autoAppliedRef.current = false;
            }}
            className="h-7 w-7 rounded-lg text-white/40 hover:text-white/70 flex items-center justify-center shrink-0"
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            type="button"
            data-testid="rv-autojob-apply"
            onClick={() => onApply?.(guess.job_id, { auto: false, suggestion: guess })}
            className="h-8 px-3 rounded-lg bi-accent-btn text-xs inline-flex items-center gap-1 shrink-0"
          >
            Apply <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
      {!matchedCurrent && guess.alternatives?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {guess.alternatives.slice(0, 3).map((a) => (
            <button
              key={a.job_id}
              type="button"
              data-testid={`rv-autojob-alt-${a.job_id}`}
              onClick={() =>
                onApply?.(a.job_id, { auto: false, suggestion: { ...a, alternatives: [] } })
              }
              className="text-[10px] px-2 py-0.5 rounded-full bi-chip hover:border-[#FF9500]/40 hover:text-white transition"
            >
              {a.job_name} · {a.confidence}%
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
